import * as Y from 'yjs';

import {
    type AddMarkOperation,
    type AnyOperation,
    type Change,
    Data,
    type Document,
    type InsertArrayDataOperation,
    type InsertFragmentOperation,
    type InsertNodeOperation,
    type InsertTextOperation,
    type MergeNodeOperation,
    type MoveNodeOperation,
    type RemoveArrayDataOperation,
    type RemoveDataOperation,
    type RemoveFragmentOperation,
    type RemoveMarkOperation,
    type RemoveNodeOperation,
    type RemoveTextOperation,
    type SetDataOperation,
    type SetFragmentOperation,
    type SetNodeOperation,
    type SplitNodeOperation,
} from '@gitbook/slate';
import { Set as ISet } from '@gitbook/util-immutable';
import { assertNever } from '@gitbook/util-ts';

import { jsonToY } from '../utils';
import { fragmentToY, marksToAttributes, nodeToY, setDataInY } from './documentToY';
import { lookupByPath, lookupParentByPath, lookupValueInData } from './lookup';
import type { YSlateAbstractNode, YSlateBlock, YSlateDocument, YSlateInline } from './utils';

/**
 * Apply operations done in a change to a Yjs map.
 */
export function documentChangeToY(change: Change, root: YSlateDocument) {
    change.operations.forEach(([operation, afterValue, beforeValue]) => {
        operationToY(
            root,
            // FIXME hack to cast Operation class as AnyOperation union
            operation as unknown as AnyOperation,
            afterValue.document,
            beforeValue.document
        );
    });
}

function operationToY(
    root: YSlateDocument,
    operation: AnyOperation,
    document: Document,
    documentBefore: Document
) {
    switch (operation.type) {
        case 'insert_text':
            applyInsertTextOperation(root, operation);
            break;
        case 'remove_text':
            applyRemoveTextOperation(root, operation);
            break;
        case 'add_mark':
            applyAddMarkOperation(root, operation);
            break;
        case 'remove_mark':
            applyRemoveMarkOperation(root, operation);
            break;
        case 'remove_node':
            applyRemoveNode(root, operation);
            break;
        case 'insert_node':
            applyInsertNode(root, operation);
            break;
        case 'set_node':
            applySetNode(root, operation);
            break;
        case 'move_node':
            applyMoveNode(root, document, documentBefore, operation);
            break;
        case 'split_node':
            applySplitNode(root, document, operation);
            break;
        case 'merge_node':
            applyMergeNode(root, document, operation);
            break;
        case 'set_fragment':
            applySetFragment(root, operation);
            break;
        case 'insert_fragment':
            applyInsertFragment(root, operation);
            break;
        case 'remove_fragment':
            applyRemoveFragment(root, operation);
            break;
        case 'set_data':
            applySetData(root, operation);
            break;
        case 'remove_data':
            applyRemoveData(root, operation);
            break;
        case 'insert_array_data':
            applyInsertArrayData(root, operation);
            break;
        case 'remove_array_data':
            applyRemoveArrayData(root, operation);
            break;
        case 'set_document': // deliberate fallthru
        case 'set_selection':
            break;
        default:
            assertNever(operation);
    }
}

function applyInsertTextOperation(root: YSlateDocument, operation: InsertTextOperation) {
    const textElement = lookupByPath(root, [...operation.path]);
    if (!(textElement instanceof Y.XmlText)) {
        throw new Error('Invalid operation path, expected a text node');
    }

    textElement.insert(operation.offset, operation.text, marksToAttributes(operation.marks));
}

function applyRemoveTextOperation(root: YSlateDocument, operation: RemoveTextOperation) {
    const textElement = lookupByPath(root, [...operation.path]);
    if (!(textElement instanceof Y.XmlText)) {
        throw new Error('Invalid operation path, expected a text node');
    }

    textElement.delete(operation.offset, operation.text.length);
}

function applyAddMarkOperation(root: YSlateDocument, operation: AddMarkOperation) {
    const textElement = lookupByPath(root, [...operation.path]);
    if (!(textElement instanceof Y.XmlText)) {
        throw new Error('Invalid operation path, expected a text node');
    }

    textElement.format(
        operation.offset,
        operation.length,
        marksToAttributes(ISet.create([operation.mark]))
    );
}

function applyRemoveMarkOperation(root: YSlateDocument, operation: RemoveMarkOperation) {
    const textElement = lookupByPath(root, [...operation.path]);
    if (!(textElement instanceof Y.XmlText)) {
        throw new Error('Invalid operation path, expected a text node');
    }

    textElement.format(operation.offset, operation.length, {
        [operation.mark.type]: null,
    });
}

function applyRemoveNode(root: YSlateDocument, operation: RemoveNodeOperation) {
    const { parent, index } = lookupParentByPath(root, [...operation.path]);
    parent.delete(index);
}

function applyInsertNode(root: YSlateDocument, operation: InsertNodeOperation) {
    const { parent, index } = lookupParentByPath(root, [...operation.path]);
    parent.insert(index, [nodeToY(operation.node) as Y.XmlElement]);
}

function applyMoveNode(
    root: YSlateDocument,
    document: Document,
    documentBefore: Document,
    operation: MoveNodeOperation
) {
    const { parent, index } = lookupParentByPath(root, [...operation.path]);
    const { parent: newParent, index: newIndex } = lookupParentByPath(root, [...operation.newPath]);

    // TODO: doesn't work when newPath is now invalid
    const nodeBefore = documentBefore.assertPath(operation.path);
    const node = document.assertNode(nodeBefore.key);
    // @ts-ignore FIXME ChildNode doesn't overlap with Block | Inline | Text
    const yNode = nodeToY(node) as Y.XmlElement;

    parent.delete(index);
    newParent.insert(newIndex, [yNode]);
}

function applySplitNode(root: YSlateDocument, document: Document, operation: SplitNodeOperation) {
    // The split_node logic in Slate is quite complex and will be hard to replicate.
    // Instead, we replace the 2 nodes at the parent level
    const { parent, index } = lookupParentByPath(root, [...operation.path]);

    const parentNode = document.assertNode(parent.getAttribute('key')!);
    if (parentNode.object === 'text') {
        throw new Error('Invalid operation path, expected a node with descendants for split_node');
    }

    const node = parentNode.nodes.get(index);
    if (!node) {
        throw new Error('Node not found in parent nodes');
    }

    const newNode = parentNode.nodes.get(index + 1);
    if (!newNode) {
        throw new Error('New node not found in parent nodes');
    }

    parent.delete(index);
    parent.insert(index, [nodeToY(node) as Y.XmlElement, nodeToY(newNode) as Y.XmlElement]);
}

function applyMergeNode(root: YSlateDocument, document: Document, operation: MergeNodeOperation) {
    // The merge_node logic in Slate is quite complex and will be hard to replicate.
    // Instead, we replace the 2 nodes at the parent level
    const { parent, index } = lookupParentByPath(root, [...operation.path]);

    const parentNode = document.assertNode(parent.getAttribute('key')!);
    if (parentNode.object === 'text') {
        throw new Error('Invalid operation path, expected a node with descendants for merge_node');
    }

    const mergedNode = parentNode.nodes.get(index - 1);
    if (!mergedNode) {
        throw new Error('Merged node not found in parent nodes');
    }

    parent.delete(index - 1, 2);
    parent.insert(index - 1, [nodeToY(mergedNode) as Y.XmlElement]);
}

function applySetNode(root: YSlateDocument, operation: SetNodeOperation) {
    const node = lookupByPath(root, [...operation.path]);

    if (typeof operation.properties === 'string') {
        // BlockCreateProps, InlineCreateProps and TextCreateProps can have
        // string `properties`, as opposed to an object. This is not supported.
        return;
    }

    if ('key' in operation.properties && operation.properties.key !== undefined) {
        (node as YSlateAbstractNode).setAttribute('key', operation.properties.key);
    }
    if ('type' in operation.properties && operation.properties.type !== undefined) {
        (node as YSlateBlock).setAttribute('type', operation.properties.type);
    }
    if ('isVoid' in operation.properties && operation.properties.isVoid !== undefined) {
        if (node instanceof Y.XmlText) {
            throw new Error('Should not set isVoid on a text');
        }
        (node as YSlateBlock).setAttribute('isVoid', operation.properties.isVoid);
    }
    if ('data' in operation.properties && operation.properties.data !== undefined) {
        if (node instanceof Y.XmlText) {
            throw new Error('Should not set data on a text');
        }
        setDataInY(node, Data.create(operation.properties.data));
    }
}

function applyInsertFragment(root: YSlateDocument, operation: InsertFragmentOperation) {
    const node = lookupByPath(root, [...operation.path]);
    if (node instanceof Y.XmlText) {
        throw new Error('Expect a fragment node');
    }

    const yNode = node as YSlateBlock | YSlateInline;

    const index = operation.fragmentIndex;
    const fragment = fragmentToY(operation.node);

    let fragmentsArray = yNode.getAttribute('fragments');
    if (!fragmentsArray) {
        fragmentsArray = new Y.XmlFragment();
        yNode.setAttribute('fragments', fragmentsArray);
    }
    fragmentsArray.insert(index, [fragment]);
}

function applySetFragment(root: YSlateDocument, operation: SetFragmentOperation) {
    const node = lookupByPath(root, [...operation.path]);
    if (node instanceof Y.XmlText) {
        throw new Error('Expect a fragment node');
    }

    const yNode = node as YSlateBlock | YSlateInline;

    const index = operation.fragmentIndex;
    const fragment = fragmentToY(operation.node);

    let fragmentsArray = yNode.getAttribute('fragments');
    if (!fragmentsArray) {
        fragmentsArray = new Y.XmlFragment();
        yNode.setAttribute('fragments', fragmentsArray);
    }

    fragmentsArray.delete(index, 1);
    fragmentsArray.insert(index, [fragment]);
}

function applyRemoveFragment(root: YSlateDocument, operation: RemoveFragmentOperation) {
    const node = lookupByPath(root, [...operation.path]);
    if (node instanceof Y.XmlText) {
        throw new Error('Expect a fragment node');
    }

    const yNode = node as YSlateBlock | YSlateInline;

    const index = operation.fragmentIndex;
    const fragmentsArray = yNode.getAttribute('fragments')!;
    fragmentsArray.delete(index);
}

function applySetData(root: YSlateDocument, operation: SetDataOperation) {
    const node = lookupByPath(root, [...operation.path]);
    if (node instanceof Y.XmlText) {
        throw new Error('Should not set data on a text');
    }

    const value = jsonToY(operation.data);
    const parentData = lookupValueInData(node, operation.dataPath.slice(0, -1));
    const dataKey = operation.dataPath[operation.dataPath.length - 1];

    parentData.set(dataKey, value);
}

function applyRemoveData(root: YSlateDocument, operation: RemoveDataOperation) {
    const node = lookupByPath(root, [...operation.path]);
    if (node instanceof Y.XmlText) {
        throw new Error('Should not set data on a text');
    }

    const parentData = lookupValueInData(node, operation.dataPath.slice(0, -1));
    const dataKey = operation.dataPath[operation.dataPath.length - 1];
    parentData.delete(dataKey);
}

function applyInsertArrayData(root: YSlateDocument, operation: InsertArrayDataOperation) {
    const node = lookupByPath(root, [...operation.path]);
    if (node instanceof Y.XmlText) {
        throw new Error('Should not set data on a text');
    }

    const parentData = lookupValueInData(node, operation.dataPath.slice(0, -1));
    const dataKey = operation.dataPath[operation.dataPath.length - 1];

    const array = parentData.get(dataKey);

    if (!array || !(array instanceof Y.Array)) {
        return;
    }

    const value = jsonToY(operation.data);
    array.insert(operation.dataIndex, [value]);
}

function applyRemoveArrayData(root: YSlateDocument, operation: RemoveArrayDataOperation) {
    const node = lookupByPath(root, [...operation.path]);
    if (node instanceof Y.XmlText) {
        throw new Error('Should not set data on a text');
    }

    const parentData = lookupValueInData(node, operation.dataPath.slice(0, -1));
    const dataKey = operation.dataPath[operation.dataPath.length - 1];
    const array = parentData.get(dataKey);

    if (!array || !(array instanceof Y.Array)) {
        return;
    }

    let foundIndex: number;
    while ((foundIndex = array.toArray().findIndex((value) => value === operation.data)) !== -1) {
        array.delete(foundIndex);
    }
}
